import { OpaqueCode } from '@/components/alphanumeric-code';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { InfoKeyValue } from '@/components/info-key-value';
import type { UserIdentityLike } from '@airreading/auth/identity';
import {
  allUserIdentityStatuses,
  updateUserIdentitiesOptionsSchema,
  userIdentityStatusLabel,
  type UserIdentityProviderKey,
} from '@airreading/common/data';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

type EditUserIdentitiesForm = z.infer<typeof updateUserIdentitiesOptionsSchema>;

interface UserIdentityProviderFieldsProps {
  providerKey: UserIdentityProviderKey;
  title: string;
  form: UseFormReturn<EditUserIdentitiesForm>;
  editing: boolean;
  provider: Omit<UserIdentityLike, 'iss'> & { iss: string | null };
}

export function UserIdentityProviderFields({
  providerKey,
  title,
  form,
  editing,
  provider,
}: UserIdentityProviderFieldsProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-xs font-semibold text-gray-400">{title}</h3>
      <div className="grid grid-cols-5 gap-4">
        <InfoKeyValue
          label="Status"
          value={userIdentityStatusLabel(provider.status)}
          edit={{
            editing,
            editModeComponent: (
              <FormSingleSelect
                name={`${providerKey}.status`}
                label="Status"
                form={form}
                options={[...allUserIdentityStatuses]}
                renderOption={userIdentityStatusLabel}
                triggerProps={{ className: 'w-full' }}
              />
            ),
          }}
        />
        <InfoKeyValue
          label="Issuer Identifier (iss)"
          value={provider.iss ? <OpaqueCode code={provider.iss} /> : '-'}
          edit={{
            editing,
            editModeComponent: (
              <FormInputField
                name={`${providerKey}.iss`}
                label="Issuer Identifier (iss)"
                form={form}
                className="col-span-2"
              />
            ),
          }}
          className="col-span-2"
        />
        <InfoKeyValue
          label="Subject Identifier (sub)"
          value={provider.sub ? <OpaqueCode code={provider.sub} /> : '-'}
          edit={{
            editing,
            editModeComponent: (
              <FormInputField
                name={`${providerKey}.sub`}
                label="Subject Identifier (sub)"
                form={form}
                className="col-span-2"
              />
            ),
          }}
          className="col-span-2"
        />
      </div>
    </div>
  );
}
